import costCalculatorService, {
  type City,
  type CalculationResult,
  type AffordabilityResult,
  type LifestyleTier,
} from '@/shared/services/CostCalculatorService';
import { motion } from 'framer-motion';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSafeThemeClasses, useSafeTheme } from '@/shared/theme';

type ThemeClasses = ReturnType<typeof useSafeThemeClasses>;

interface StyledSelectProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: React.ReactNode[];
  label: string;
  placeholder: string;
}

interface StyledIncomeSliderProps {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step: number;
  label: string;
  currency: string;
  symbol: string;
}

interface ActionButtonProps {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary';
}

// A currency-aware min/max/step/default for the income slider. Fixed
// numeric bounds (e.g. 100-500,000 for every currency) don't make sense
// once JPY, INR, or KRW are in the mix - this scales the whole range to
// roughly a $0-$50k/month USD-equivalent ceiling (comfortably above the
// "luxury" tier cost in every city in the dataset), then rounds everything
// to a step size derived from that range's own magnitude so min/step/value
// are always mutually aligned (the previous fixed min=100/step=500/default
// 50000 combination didn't align, which is what caused the slider thumb to
// silently snap away from the displayed value).
function getIncomeRange(currency: string): { min: number; max: number; step: number; default: number } {
  const usdToLocal = (usd: number) => costCalculatorService.convertCurrency(usd, 'USD', currency);
  const rawMax = usdToLocal(50000);
  const rawDefault = usdToLocal(4000);
  const magnitude = Math.pow(10, Math.max(0, Math.floor(Math.log10(Math.max(rawMax, 1))) - 2));
  const step = Math.max(1, magnitude);
  const max = Math.ceil(rawMax / step) * step;
  const defaultValue = Math.round(rawDefault / step) * step;
  return { min: 0, max, step, default: defaultValue };
}

// The slider's physical drag position is always a flat 0-1000 scale
// internally; the income VALUE it represents follows a cubic curve instead
// of a straight line. At t=10% dragged, income is only ~0.1% of the range;
// at t=90%, income is already ~73%. That's the point: a linear slider makes
// weak currencies (INR, JPY, KRW) reach six figures within the first few
// percent of travel, which reads as broken. A cubic curve gives fine control
// for realistic budget/comfortable incomes near the low end, and lets the
// last stretch of travel cover the wide luxury range quickly.
const SLIDER_RESOLUTION = 1000;
const SLIDER_EXPONENT = 3;

function sliderPositionToIncome(position: number, min: number, max: number): number {
  const t = Math.min(Math.max(position / SLIDER_RESOLUTION, 0), 1);
  return min + (max - min) * Math.pow(t, SLIDER_EXPONENT);
}

function incomeToSliderPosition(income: number, min: number, max: number): number {
  if (max <= min) return 0;
  const t = Math.min(Math.max((income - min) / (max - min), 0), 1);
  return Math.round(Math.pow(t, 1 / SLIDER_EXPONENT) * SLIDER_RESOLUTION);
}

function roundToNiceStep(value: number, step: number): number {
  if (step <= 0) return Math.round(value);
  return Math.round(value / step) * step;
}

function StyledSelect({ value, onChange, options, label, placeholder }: StyledSelectProps) {
  const theme = useSafeThemeClasses();
  return (
    <div className="space-y-3">
      <label className={`block text-sm font-medium ${theme.textPrimary}`}>{label}</label>
      <select
        value={value}
        onChange={onChange}
        className={`
          w-full p-3 rounded-xl border border-base-content/20
          ${theme.textPrimary}
          focus:outline-none focus:ring-2 focus:ring-primary
          transition-shadow duration-200
        `}
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {options}
      </select>
    </div>
  );
}

function StyledIncomeSlider({ value, onChange, min, max, step, label, currency, symbol }: StyledIncomeSliderProps) {
  const theme = useSafeThemeClasses();
  const { variant } = useSafeTheme();
  const isFantasy = variant === 'fantasy';

  const position = incomeToSliderPosition(value, min, max);
  const progressPct = (position / SLIDER_RESOLUTION) * 100;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawIncome = sliderPositionToIncome(Number(e.currentTarget.value), min, max);
    onChange(roundToNiceStep(rawIncome, step));
  };

  // Reference points along the curve so the non-linear scale reads as
  // intentional rather than confusing - these are the actual income values
  // at 0/25/50/75/100% of physical drag, not evenly-spaced income amounts.
  const referencePoints = [0, 0.25, 0.5, 0.75, 1].map((t) =>
    Math.round(min + (max - min) * Math.pow(t, SLIDER_EXPONENT))
  );

  const fillClasses = isFantasy
    ? 'bg-gradient-to-r from-[#8b6914] to-[#d4af37]'
    : 'bg-gradient-to-r from-blue-500 to-indigo-600';
  // Tailwind's JIT scanner only picks up complete, literal class strings -
  // interpolating a fragment into "[&::-webkit-slider-thumb]:${x}" would
  // never actually generate the CSS, so each theme's full thumb-shadow
  // class is spelled out here instead of built from a shared prefix.
  const thumbShadowClass = isFantasy
    ? '[&::-webkit-slider-thumb]:shadow-[#8b6914]/40'
    : '[&::-webkit-slider-thumb]:shadow-blue-500/40';

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className={`text-sm font-medium ${theme.textPrimary}`}>{label}</span>
        <span className={`text-sm font-semibold ${theme.textAccent}`}>
          {`${symbol}${Math.round(value).toLocaleString('en-US')} ${currency}`}
        </span>
      </div>

      <div className="relative h-7 flex items-center">
        {/* Track background */}
        <div className={`absolute inset-x-0 h-2 rounded-full ${theme.input}`} />
        {/* Filled progress - tracks physical slider position, which is why
            it doesn't move in step with the (non-linear) income value. */}
        <div
          className={`absolute h-2 rounded-full ${fillClasses}`}
          style={{ width: `${progressPct}%` }}
        />
        <input
          type="range"
          min={0}
          max={SLIDER_RESOLUTION}
          step={1}
          value={position}
          onChange={handleChange}
          aria-label={label}
          aria-valuetext={`${symbol}${Math.round(value).toLocaleString('en-US')} ${currency}`}
          className={`
            relative w-full h-7 appearance-none bg-transparent cursor-pointer touch-none
            [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-runnable-track]:h-2
            [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:w-6
            [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-0
            [&::-webkit-slider-thumb]:shadow-lg ${thumbShadowClass}
            [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:duration-150
            hover:[&::-webkit-slider-thumb]:scale-110 active:[&::-webkit-slider-thumb]:scale-95
            [&::-moz-range-track]:bg-transparent [&::-moz-range-track]:h-2
            [&::-moz-range-thumb]:h-6 [&::-moz-range-thumb]:w-6 [&::-moz-range-thumb]:rounded-full
            [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:shadow-lg
            focus:outline-none
          `}
        />
      </div>

      <div className={`flex justify-between text-[10px] ${theme.textMuted}`}>
        {referencePoints.map((amount, i) => (
          <span key={i}>{symbol}{amount.toLocaleString('en-US')}</span>
        ))}
      </div>
    </div>
  );
}

function ActionButton({ onClick, disabled = false, children, variant = 'primary' }: ActionButtonProps) {
  const theme = useSafeThemeClasses();
  const baseClasses =
    'px-8 py-4 rounded-2xl font-semibold tracking-wide transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed';
  const variants = {
    primary: `${theme.buttonPrimary} ${theme.buttonTextPrimary} hover:shadow-xl hover:brightness-110`,
    secondary: `${theme.card} ${theme.textPrimary} hover:shadow-lg`,
  };

  return (
    <button onClick={onClick} disabled={disabled} className={`${baseClasses} ${variants[variant]}`}>
      {children}
    </button>
  );
}

const CATEGORY_LABELS: Record<string, string> = {
  housing: 'Housing',
  food: 'Food',
  transport: 'Transport',
  entertainment: 'Entertainment',
  utilities: 'Utilities',
  healthcare: 'Healthcare',
};

function formatMoney(amount: number, symbol: string): string {
  return `${symbol}${Math.round(amount).toLocaleString('en-US')}`;
}

function AffordabilityBreakdown({
  affordability,
  theme,
}: {
  affordability: AffordabilityResult;
  theme: ThemeClasses;
}) {
  const { city, tiers } = affordability;

  return (
    <div className={`rounded-2xl p-8 transition-all duration-300 hover:shadow-2xl ${theme.card}`}>
      <h2 className={`text-2xl font-bold mb-2 ${theme.textHeading}`}>
        Can You Afford {city.name}?
      </h2>
      <p className={`text-sm mb-6 ${theme.textSecondary}`}>
        Based on your income at each lifestyle level, here&apos;s what&apos;s realistic.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {tiers.map((t) => (
          <div
            key={t.tier}
            className={`rounded-2xl p-6 border-2 ${
              t.feasible
                ? 'border-transparent ' + theme.alertSuccess
                : 'border-transparent ' + theme.alertError
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className={`text-lg font-bold ${theme.textHeading}`}>{t.label}</h3>
              <span
                className={`text-xs font-semibold px-3 py-1 rounded-full ${
                  t.feasible ? theme.alertSuccess : theme.alertError
                }`}
              >
                {t.feasible ? 'Affordable' : 'Not affordable'}
              </span>
            </div>

            {t.feasible && t.breakdown ? (
              <>
                <div className="space-y-1.5 mb-3">
                  {Object.entries(t.breakdown).map(([category, amount]) => (
                    <div key={category} className="flex justify-between text-sm">
                      <span className={theme.textSecondary}>{CATEGORY_LABELS[category] || category}</span>
                      <span className={theme.textPrimary}>{formatMoney(amount, city.symbol)}</span>
                    </div>
                  ))}
                </div>
                <div className={`flex justify-between text-sm font-semibold pt-2 border-t ${theme.textHeading}`}>
                  <span>Left over / savings</span>
                  <span className={theme.textSuccess}>{formatMoney(t.savings, city.symbol)}/mo</span>
                </div>
              </>
            ) : (
              <p className={`text-sm ${theme.textSecondary}`}>
                This lifestyle costs about <strong>{formatMoney(t.totalCost, city.symbol)}/mo</strong> in{' '}
                {city.name} — you&apos;d be short by{' '}
                <strong className={theme.textError}>{formatMoney(t.shortfall, city.symbol)}/mo</strong>. Categories:
                N/A, this budget doesn&apos;t cover the basics here.
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CalculatorPage() {
  const theme = useSafeThemeClasses();
  const [isVisible, setIsVisible] = useState(false);
  const [cities] = useState<City[]>(costCalculatorService.getCities());
  const [cityKeys] = useState<string[]>(costCalculatorService.getCityKeys());
  const [lifestyleTiers] = useState(costCalculatorService.getLifestyleTiers());
  const [lifestyleDescriptions] = useState(costCalculatorService.getLifestyleDescriptions());

  // Form state
  const [fromCity, setFromCity] = useState<string>('');
  const [toCity, setToCity] = useState<string>('');
  const [lifestyleTier, setLifestyleTier] = useState<LifestyleTier>('affordable');
  const [incomeCurrency, setIncomeCurrency] = useState<string>('USD');
  const [incomeSymbol, setIncomeSymbol] = useState<string>('$');
  const incomeRange = useMemo(() => getIncomeRange(incomeCurrency), [incomeCurrency]);
  const [incomeSlider, setIncomeSlider] = useState<number>(incomeRange.default);

  // Results state
  const [result, setResult] = useState<CalculationResult | null>(null);
  const [affordability, setAffordability] = useState<AffordabilityResult | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [liveComparison, setLiveComparison] = useState<boolean>(false);
  const [ratesLoaded, setRatesLoaded] = useState(costCalculatorService.hasLiveRates());

  const handleCalculate = useCallback(() => {
    if (!fromCity || !toCity) return;

    setIsCalculating(true);

    try {
      const calculationResult = costCalculatorService.calculateCostComparison(
        fromCity,
        toCity,
        lifestyleTier
      );

      setResult(calculationResult);
      setLiveComparison(true);
    } catch {
      setLiveComparison(false);
    } finally {
      setIsCalculating(false);
    }
  }, [fromCity, toCity, lifestyleTier]);

  // Wait for the live FX fetch to settle, then recompute so results reflect
  // today's rates rather than the static fallback snapshot the first render
  // may have used.
  useEffect(() => {
    costCalculatorService.ensureRatesLoaded().then(() => {
      setRatesLoaded(true);
    });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (liveComparison && fromCity && toCity) {
      handleCalculate();
    }
  }, [liveComparison, fromCity, toCity, handleCalculate, ratesLoaded]);

  // "Can you afford it" recomputes whenever the destination or income
  // changes - this is the primary, always-on feasibility read, independent
  // of whether the user has pressed "Compare Cities".
  useEffect(() => {
    if (!toCity) {
      setAffordability(null);
      return;
    }
    try {
      setAffordability(costCalculatorService.calculateAffordability(toCity, incomeSlider, incomeCurrency));
    } catch {
      setAffordability(null);
    }
  }, [toCity, incomeSlider, incomeCurrency, ratesLoaded]);

  useEffect(() => {
    if (fromCity) {
      const cityIndex = cityKeys.findIndex(key => key === fromCity);
      if (cityIndex !== -1) {
        const cityData = cities[cityIndex];
        if (cityData && cityData.currency !== incomeCurrency) {
          setIncomeCurrency(cityData.currency);
          setIncomeSymbol(cityData.symbol);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromCity, cityKeys, cities]);

  // Recenter the slider to a sensible default whenever the currency changes
  // (i.e. a new "current location" was picked) - carrying over a raw number
  // from a different currency's scale (e.g. a JPY-sized value read as USD)
  // is meaningless, not just imprecise.
  useEffect(() => {
    setIncomeSlider(incomeRange.default);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomeCurrency]);

  const formatCurrency = (_amount: number, _currency: string, symbol: string): string => {
    return `${symbol}${_amount.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })}`;
  };

  const formatPercentage = (percent: number): string => {
    const sign = percent > 0 ? '+' : '';
    return `${sign}${percent.toFixed(1)}%`;
  };

  const getPercentageColor = (percent: number): string => {
    if (percent > 0) return theme.textError;
    if (percent < 0) return theme.textSuccess;
    return theme.textSecondary;
  };

  const selectedTierDescription = lifestyleDescriptions[lifestyleTier];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
    }
  };

  return (
    <div className={`min-h-screen ${theme.background}`}>
      <motion.div
        className="relative max-w-6xl mx-auto px-6 pt-32 pb-24"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
        >
          <motion.h1
            className={`text-5xl md:text-7xl font-bold mb-8 ${theme.textHeading}`}
            variants={itemVariants}
          >
            Global Cost Calculator
          </motion.h1>
          <motion.p
            className={`text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed ${theme.textSecondary}`}
            variants={itemVariants}
          >
            Discover the true cost of living anywhere. Compare cities, calculate affordability, and make informed decisions about your next move.
          </motion.p>
        </motion.div>

        {/* Main Calculator */}
        <motion.section
          className={`mb-16 rounded-3xl p-8 md:p-12 ${theme.card}`}
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
        >
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8`}>
            <div className="space-y-6">
              <StyledSelect
                value={fromCity}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFromCity(e.target.value)}
                label="Current Location"
                placeholder="Where do you live now?"
                options={cityKeys.map((cityKey, index) => {
                  const city = cities[index];
                  return (
                    <option key={`from_${cityKey}`} value={cityKey} className={theme.textPrimary}>
                      {city.name}, {city.country}
                    </option>
                  );
                })}
              />

              <StyledSelect
                value={toCity}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setToCity(e.target.value)}
                label="Destination"
                placeholder="Where are you considering?"
                options={cityKeys.map((cityKey, index) => {
                  const city = cities[index];
                  return (
                    <option key={`to_${cityKey}`} value={cityKey} className={theme.textPrimary}>
                      {city.name}, {city.country}
                    </option>
                  );
                })}
              />
            </div>

            <div className="space-y-6">
              <div className="space-y-3">
                <StyledSelect
                  value={lifestyleTier}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setLifestyleTier(e.target.value as LifestyleTier)}
                  label="Lifestyle Preference"
                  placeholder="Select your lifestyle"
                  options={Object.entries(lifestyleTiers).map(([key, tierLabel]) => (
                    <option key={key} value={key} className={theme.textPrimary}>{tierLabel}</option>
                  ))}
                />
                {selectedTierDescription && (
                  <div className={`text-xs space-y-1 ${theme.textSecondary}`}>
                    <p><strong className={theme.textPrimary}>Housing:</strong> {selectedTierDescription.housing}</p>
                    <p><strong className={theme.textPrimary}>Food:</strong> {selectedTierDescription.food}</p>
                    <p><strong className={theme.textPrimary}>Transport:</strong> {selectedTierDescription.transport}</p>
                    <p><strong className={theme.textPrimary}>Entertainment:</strong> {selectedTierDescription.entertainment}</p>
                  </div>
                )}
              </div>

              <StyledIncomeSlider
                value={incomeSlider}
                onChange={setIncomeSlider}
                min={incomeRange.min}
                max={incomeRange.max}
                step={incomeRange.step}
                label={`Monthly Income${fromCity ? ` (${incomeCurrency})` : ''}`}
                currency={incomeCurrency}
                symbol={incomeSymbol}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <ActionButton
              onClick={handleCalculate}
              disabled={!fromCity || !toCity || isCalculating}
            >
              {isCalculating ? (
                <span className="flex items-center gap-3">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Analyzing...
                </span>
              ) : (
                'Compare Cities'
              )}
            </ActionButton>

            {result && (
              <ActionButton
                onClick={() => setLiveComparison(!liveComparison)}
                variant="secondary"
                disabled={false}
              >
                {liveComparison ? 'Disable Live Updates' : 'Enable Live Updates'}
              </ActionButton>
            )}
          </div>
        </motion.section>

        {/* Affordability: always-on, driven by destination + income slider */}
        {affordability && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="mb-16"
          >
            <AffordabilityBreakdown affordability={affordability} theme={theme} />
          </motion.div>
        )}

        {/* Results: from/to city comparison */}
        {result && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-12"
          >
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Current City Cost */}
              <motion.div variants={itemVariants} className={`p-6 rounded-2xl ${theme.card}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`text-sm font-medium ${theme.textMuted}`}>Current City Cost</h3>
                  <div className={`w-3 h-3 rounded-full ${theme.buttonPrimary}`}></div>
                </div>
                <p className={`text-2xl font-bold ${theme.textHeading}`}>
                  {formatCurrency(result.fromAmount, result.fromCurrency, result.fromCity.symbol)}
                </p>
                <p className={`text-xs ${theme.textSecondary}`}>{result.fromCity.name}</p>
              </motion.div>

              {/* Destination Cost */}
              <motion.div variants={itemVariants} className={`p-6 rounded-2xl ${theme.card}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`text-sm font-medium ${theme.textMuted}`}>Destination Cost</h3>
                  <div className="w-3 h-3 rounded-full bg-secondary"></div>
                </div>
                <p className={`text-2xl font-bold ${theme.textHeading}`}>
                  {formatCurrency(result.toAmount, result.toCurrency, result.toCity.symbol)}
                </p>
                <p className={`text-xs ${theme.textSecondary}`}>{result.toCity.name}</p>
              </motion.div>

              {/* Cost Difference */}
              <motion.div variants={itemVariants} className={`p-6 rounded-2xl ${theme.card}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`text-sm font-medium ${theme.textMuted}`}>Cost Difference</h3>
                  <div className={`w-3 h-3 rounded-full ${result.totalPercentChange > 0 ? theme.alertError : theme.alertSuccess}`}></div>
                </div>
                <p className={`text-2xl font-bold ${getPercentageColor(result.totalPercentChange)}`}>
                  {formatPercentage(result.totalPercentChange)}
                </p>
                <p className={`text-xs ${theme.textSecondary}`}>
                  {Math.abs(result.totalPercentChange) < 5 ? 'Similar' :
                   result.totalPercentChange > 0 ? 'More expensive' : 'Less expensive'}
                </p>
              </motion.div>

              {/* Required salary */}
              <motion.div variants={itemVariants} className={`p-6 rounded-2xl ${theme.card}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`text-sm font-medium ${theme.textMuted}`}>Salary Needed There</h3>
                  <div className={`w-3 h-3 rounded-full ${theme.buttonPrimary}`}></div>
                </div>
                <p className={`text-2xl font-bold ${theme.textHeading}`}>
                  {formatCurrency(result.salaryComparison.requiredSalaryInToCity, result.toCurrency, result.toCity.symbol)}
                </p>
                <p className={`text-xs ${theme.textSecondary}`}>to match your lifestyle in {result.toCity.name}</p>
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* Footer Info */}
        <motion.section
          className={`mt-20 rounded-3xl p-8 md:p-12 text-center ${theme.card}`}
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
        >
          <motion.div variants={itemVariants}>
            <h2 className={`text-2xl font-bold mb-4 ${theme.textHeading}`}>
              Powered by Live Exchange Rates
            </h2>
            <p className={`text-lg max-w-3xl mx-auto ${theme.textSecondary}`}>
              Our calculations incorporate housing, food, transportation, entertainment, utilities, and healthcare costs,
              converted using live exchange rates (refreshed daily). Local tax rates are used for salary comparisons.
            </p>
          </motion.div>
        </motion.section>
      </motion.div>

    </div>
  );
}
